import { createServerFn } from "@tanstack/react-start";
import { createHash } from "node:crypto";
import { z } from "zod";

const InputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  company: z.string().optional(),
  signupPage: z.string().optional(),
  tagsToAdd: z.array(z.string()).optional(),
  tagsToRemove: z.array(z.string()).optional(),
  status: z.enum(["subscribed", "pending"]).optional(),
  marketingPermission: z.boolean().optional(),
});

function md5Lower(s: string): string {
  return createHash("md5").update(s.trim().toLowerCase()).digest("hex");
}

export const mailchimpSubscribe = createServerFn({ method: "POST" })
  .validator(InputSchema)
  .handler(async ({ data: body }) => {
    const API_KEY = process.env["MAILCHIMP_API_KEY"];
    const DC = process.env["MAILCHIMP_DC"] ?? "us7";
    const LIST_ID =
      process.env["MAILCHIMP_AUDIENCE_ID"] ??
      process.env["MAILCHIMP_LIST_ID"] ??
      "80e24faf6a";

    if (!API_KEY) {
      console.error("mailchimp-subscribe: MAILCHIMP_API_KEY not set");
      return { ok: true, skipped: "not_configured" };
    }

    const email = body.email.trim().toLowerCase();
    const hash = md5Lower(email);
    const base = `https://${DC}.api.mailchimp.com/3.0/lists/${LIST_ID}`;
    const authHeader = `Bearer ${API_KEY}`;

    const mergeFields: Record<string, string> = {};
    if (body.firstName) mergeFields["FNAME"] = body.firstName;
    if (body.lastName) mergeFields["LNAME"] = body.lastName;
    if (body.phone) mergeFields["PHONE"] = body.phone;
    if (body.company) mergeFields["COMPANY"] = body.company;
    if (body.signupPage) mergeFields["SIGNUPPG"] = body.signupPage.slice(0, 255);

    const upsertPayload: Record<string, unknown> = {
      email_address: email,
      status_if_new: body.status ?? "subscribed",
    };
    if (Object.keys(mergeFields).length > 0) {
      upsertPayload["merge_fields"] = mergeFields;
    }

    try {
      const upsertRes = await fetch(`${base}/members/${hash}`, {
        method: "PUT",
        headers: { Authorization: authHeader, "Content-Type": "application/json" },
        body: JSON.stringify(upsertPayload),
      });
      if (!upsertRes.ok) {
        const text = await upsertRes.text();
        console.error("mailchimp upsert failed", upsertRes.status, text);
      }

      const tagsToAdd = (body.tagsToAdd ?? []).filter(Boolean);
      const tagsToRemove = (body.tagsToRemove ?? []).filter(Boolean);

      if (tagsToAdd.length + tagsToRemove.length > 0) {
        const tags = [
          ...tagsToAdd.map((name) => ({ name, status: "active" as const })),
          ...tagsToRemove.map((name) => ({ name, status: "inactive" as const })),
        ];
        const tagRes = await fetch(`${base}/members/${hash}/tags`, {
          method: "POST",
          headers: { Authorization: authHeader, "Content-Type": "application/json" },
          body: JSON.stringify({ tags }),
        });
        if (!tagRes.ok) {
          const text = await tagRes.text();
          console.error("mailchimp tag update failed", tagRes.status, text);
        }
      }
    } catch (e) {
      console.error("mailchimp-subscribe threw", e);
    }

    return { ok: true };
  });
